/** Local calendar windows for appointment / outcome list filters. */

export type AppointmentWindow = 'today' | 'upcoming' | 'all'

export function parseAppointmentWindow(
  value: string | null | undefined
): AppointmentWindow {
  if (value === 'today' || value === 'upcoming' || value === 'all') return value
  return 'upcoming'
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
}

function startOfNextLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0)
}

export type AppointmentWindowQuery = {
  booking_start_after?: string
  booking_start_before?: string
  order: 'created' | 'booking_start'
  limit: number
}

/** Build list API query params for a named appointment window. */
export function appointmentWindowQueryParams(
  window: AppointmentWindow,
  now = new Date()
): AppointmentWindowQuery {
  if (window === 'all') {
    return { order: 'created', limit: 100 }
  }
  if (window === 'today') {
    return {
      booking_start_after: startOfLocalDay(now).toISOString(),
      booking_start_before: startOfNextLocalDay(now).toISOString(),
      order: 'booking_start',
      limit: 100,
    }
  }
  // upcoming: now → +7 days
  const end = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
  return {
    booking_start_after: now.toISOString(),
    booking_start_before: end.toISOString(),
    order: 'booking_start',
    limit: 100,
  }
}

/** Appointment start label: time-only when same local day; else weekday + time. */
export function formatAppointmentStartAt(
  iso: string | null | undefined,
  nowMs = Date.now()
): string {
  if (!iso) return '—'
  const start = new Date(iso)
  if (Number.isNaN(start.getTime())) return '—'
  const now = new Date(nowMs)
  const sameDay =
    start.getFullYear() === now.getFullYear() &&
    start.getMonth() === now.getMonth() &&
    start.getDate() === now.getDate()
  if (sameDay) {
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(start)
  }
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(start)
}
